"use client";

import CookieSettingsButton from "@/components/cookie-consent/CookieSettingsButton";
import PinterestIcon from "@/components/icons/PinterestIcon";
import TikTokIcon from "@/components/icons/TikTokIcon";
import XingIcon from "@/components/icons/XingIcon";
import FooterSectionTitle from "@/components/shared/public/fonts/FooterSectionTitle";
import { Link } from "@/i18n/navigation";
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { JSX } from "react";
import {
  hosting,
  hostingEN,
  mastercard,
  paypal,
  prePay,
  visa,
} from "../../../../public/images/logos";
import FooterLink from "./FooterLink";

const SocialIcon = ({ icon, href }: { icon: JSX.Element; href: string }) => {
  return (
    <Link
      className="lg:hover:border-primary lg:hover:text-primary text-ring border-input flex transform cursor-pointer flex-col items-center justify-center rounded-full border-2 p-2 transition-all duration-300"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {icon}
    </Link>
  );
};

const LegalLinks = () => {
  const t = useTranslations("common");
  const locale = useLocale();

  /*
    disabled 2026-07-28 KH
    <FooterLink
      title={t("cancellationPolicy")}
      href={"/widerrufsbelehrung"}
    />
  */
  return (
    <div className="s:w-auto m-auto w-60 max-w-screen-xl px-4 py-12">
      <div className="s:justify-center flex flex-wrap items-start gap-10 md:justify-between">
        <div className="s:w-auto flex w-full flex-col gap-3">
          <FooterSectionTitle className="pb-8" title={t("legal")} />
          <FooterLink title={t("dataSecurity")} href={"/datensicherheit"} />
          <FooterLink title={t("privacy")} href={"/datenschutz"} />
          <FooterLink title={t("cookiePolicy")} href={"/cookie-richtlinie"} />
          <CookieSettingsButton>{t("cookieSettings")}</CookieSettingsButton>
          <FooterLink title={t("terms")} href={"/agb"} />
          <FooterLink title={t("imprint")} href={"/impressum"} />
        </div>

        <div className="s:w-auto flex w-full flex-col gap-3">
          <FooterSectionTitle className="pb-8" title={t("about")} />
          <FooterLink title={t("prices")} href={"/preise"} />
          <FooterLink title={"Team"} href={"/team"} />
          <FooterLink title={"FAQ"} href={"/faq"} />
          <FooterLink title={t("jobAds")} href={"/stellenangebote"} />
        </div>

        <div className="s:w-auto flex w-full flex-col gap-3">
          <FooterSectionTitle
            className="pb-8"
            title={t("footerInfo.network")}
          />
          <FooterLink
            title={t("footerInfo.affiliate")}
            href={"/blog/affiliate"}
          />
          <FooterLink title={t("footerInfo.podcast")} href={"/podcast"} />
          <span className="inline-flex items-baseline gap-2">
            <FooterLink
              title={t("footerInfo.socialEngagement")}
              href={"/soziales-engagement"}
            />
            <span className="bg-primary rounded-full px-2 py-0.5 text-[10px] leading-none font-semibold text-white uppercase">
              {t("footerInfo.socialEngagementBadge")}
            </span>
          </span>
          <FooterLink title={t("footerInfo.partner")} href={"/partner"} />
          <div className="mt-2 grid grid-cols-5 gap-2">
            <SocialIcon
              href="https://www.facebook.com/GrundrissSchmiede/"
              icon={<Facebook size={20} />}
            />
            <SocialIcon
              href="https://www.instagram.com/grundrissschmiede/#"
              icon={<Instagram size={20} />}
            />
            <SocialIcon
              href="https://de.pinterest.com/grundriss_schmiede/"
              icon={<PinterestIcon width={20} height={20} />}
            />
            <SocialIcon
              href="https://www.linkedin.com/company/grundriss-schmiede/"
              icon={<Linkedin size={20} />}
            />
            <SocialIcon
              href="https://www.xing.com/pages/immomates-gmbh"
              icon={<XingIcon width={20} height={20} />}
            />
            <SocialIcon
              href="https://www.youtube.com/channel/UCKZX84eqTlPrAQz5A6KDFWw"
              icon={<Youtube size={20} />}
            />
            <SocialIcon
              href="https://www.tiktok.com/@grundrissschmiede"
              icon={<TikTokIcon width={20} height={20} />}
            />
          </div>
        </div>

        <div className="s:w-auto flex w-full flex-col items-center gap-4">
          <Link href="/datensicherheit">
            <Image
              width={200}
              height={200}
              src={locale === "de" ? hosting : hostingEN}
              alt={t("imageAlt.hostedInGermany")}
              className="gs--hosting-badge"
            />
          </Link>

          <Link
            href="/preise#zahlungsmethoden"
            className="grid grid-cols-4 items-center justify-items-center gap-3 rounded-md focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-4 focus-visible:outline-none"
            aria-label={t("paymentMethods")}
          >
            <span className="flex h-11 w-14 items-center justify-center">
              <Image
                src={visa}
                alt={t("imageAlt.visa")}
                className="max-h-7 w-auto"
              />
            </span>
            <span className="flex h-11 w-14 items-center justify-center">
              <Image
                src={mastercard}
                alt={t("imageAlt.mastercard")}
                className="max-h-7 w-auto"
              />
            </span>
            <span className="flex h-11 w-14 items-center justify-center">
              <Image
                src={paypal}
                alt={t("imageAlt.paypal")}
                className="max-h-9 w-auto"
              />
            </span>
            <span className="flex h-11 w-14 items-center justify-center">
              <Image
                src={prePay}
                alt={t("imageAlt.prepayment")}
                className="max-h-9 w-auto"
              />
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LegalLinks;
